import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validateAffiliateCode } from '../lib/affiliateSchema';

// Affiliate code expiration time (7 days)
const AFFILIATE_CODE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Custom hook for tracking affiliate codes across the entire application
 * Handles URL parameter detection, validation, persistence, and expiration
 */
export function useAffiliateTracking(user) {
  const [searchParams] = useSearchParams();
  const [affiliateCode, setAffiliateCode] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Check if affiliate code is expired
  const isExpired = (timestamp) => {
    return Date.now() - timestamp > AFFILIATE_CODE_EXPIRY;
  };

  // Get stored affiliate code from localStorage
  const getStoredAffiliateCode = () => {
    try {
      const stored = localStorage.getItem('affiliateCode');
      if (!stored) return null;
      
      // Handle both old format (plain string) and new format (JSON)
      let affiliateData;
      try {
        affiliateData = JSON.parse(stored);
        // If it's already an object, use it
        if (typeof affiliateData === 'object' && affiliateData.code) {
          // Check if expired
          if (isExpired(affiliateData.timestamp)) {
            console.log('🗑️ Affiliate code expired, removing from storage');
            localStorage.removeItem('affiliateCode');
            return null;
          }
          return affiliateData.code;
        }
      } catch (parseError) {
        // If JSON parsing fails, treat as old format (plain string)
        affiliateData = {
          code: stored,
          timestamp: Date.now(),
          source: 'legacy_format',
          validated: false
        };
        // Store in new format
        localStorage.setItem('affiliateCode', JSON.stringify(affiliateData));
        return stored;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing stored affiliate code:', error);
      localStorage.removeItem('affiliateCode');
      return null;
    }
  };

  // Store affiliate code with metadata
  const storeAffiliateCode = async (code, source = 'unknown') => {
    try {
      setIsValidating(true);
      
      // Validate code if user is logged in
      let isValid = false;
      if (user) {
        isValid = await validateAffiliateCode(code);
      }

      const affiliateData = {
        code,
        timestamp: Date.now(),
        source,
        validated: isValid,
        userId: user?.uid || null
      };

      localStorage.setItem('affiliateCode', JSON.stringify(affiliateData));
      setAffiliateCode(code);
      
      console.log(`📝 Affiliate code stored: ${code} (source: ${source}, validated: ${isValid})`);
      return true;
    } catch (error) {
      console.error('Error storing affiliate code:', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  // Detect affiliate codes from URL parameters
  useEffect(() => {
    const detectAffiliateCode = async () => {
      // Check multiple URL parameters for affiliate codes
      const refCode = searchParams.get('ref');
      const affiliateParam = searchParams.get('affiliate');
      
      // Also check window.location directly as fallback
      const urlParams = new URLSearchParams(window.location.search);
      const refCodeDirect = urlParams.get('ref');
      const affiliateParamDirect = urlParams.get('affiliate');
      
      
      // PRIORITIZE REF PARAMETER - use ref if available, otherwise use affiliate
      let detectedCode = null;
      if (refCode || refCodeDirect) {
        detectedCode = refCode || refCodeDirect;
      } else if (affiliateParam || affiliateParamDirect) {
        detectedCode = affiliateParam || affiliateParamDirect;
      }
      
      
      if (detectedCode && detectedCode !== 'affiliate') { // 'affiliate' is a placeholder
        
        // Check if we already have a valid code stored
        const storedCode = getStoredAffiliateCode();
        
        
        if (!storedCode) {
          // No existing code, store the new one (first-click attribution)
          await storeAffiliateCode(detectedCode, 'url_detection');
        } else if (storedCode !== detectedCode) {
          // Different code detected, check if stored code is expired
          try {
            const stored = localStorage.getItem('affiliateCode');
            const affiliateData = JSON.parse(stored);
            
            if (isExpired(affiliateData.timestamp)) {
              // Stored code is expired, allow new code (new first-click)
              console.log(`⏰ Previous code expired, new first-click attribution: ${detectedCode}`);
              await storeAffiliateCode(detectedCode, 'url_detection');
            } else {
              // Stored code is still valid, keep the first-click (ignore new code)
              console.log(`🛡️ First-click protection: ignoring ${detectedCode}, keeping ${storedCode} (${Math.floor((Date.now() - affiliateData.timestamp) / (60 * 60 * 1000))} hours old)`);
            }
          } catch (error) {
            console.error('Error checking stored affiliate code:', error);
            // If we can't parse stored data, store the new code
            await storeAffiliateCode(detectedCode, 'url_detection');
          }
        } else {
        }
      } else {
      }
    };

    detectAffiliateCode();
  }, [searchParams, user]);

  // Validate stored affiliate code when user logs in
  useEffect(() => {
    const validateStoredCode = async () => {
      if (!user) return;
      
      try {
        const stored = localStorage.getItem('affiliateCode');
        if (!stored) return;
        
        // Handle both old format (plain string) and new format (JSON)
        let affiliateData;
        try {
          affiliateData = JSON.parse(stored);
          // If it's not an object or doesn't have code, treat as old format
          if (typeof affiliateData !== 'object' || !affiliateData.code) {
            affiliateData = { code: stored, timestamp: Date.now(), validated: false };
          }
        } catch (parseError) {
          // If JSON parsing fails, treat as old format (plain string)
          affiliateData = { code: stored, timestamp: Date.now(), validated: false };
        }
        
        // Skip if already validated or expired
        if (affiliateData.validated || isExpired(affiliateData.timestamp)) {
          return;
        }
        
        // Validate the stored code
        const isValid = await validateAffiliateCode(affiliateData.code);
        
        
        // Update stored data with validation result
        const updatedData = {
          ...affiliateData,
          validated: isValid,
          userId: user.uid,
          validatedAt: Date.now()
        };
        
        localStorage.setItem('affiliateCode', JSON.stringify(updatedData));
        
        if (isValid) {
        } else {
          localStorage.removeItem('affiliateCode');
          setAffiliateCode(null);
        }
      } catch (error) {
        console.error('Error validating stored affiliate code:', error);
      }
    };

    validateStoredCode();
  }, [user]);

  // Initialize affiliate code from storage
  useEffect(() => {
    const storedCode = getStoredAffiliateCode();
    if (storedCode) {
      setAffiliateCode(storedCode);
    }
  }, []);

  // Get current affiliate code (for use in checkout, etc.)
  const getAffiliateCode = () => {
    return getStoredAffiliateCode();
  };

  // Clear affiliate code
  const clearAffiliateCode = () => {
    localStorage.removeItem('affiliateCode');
    setAffiliateCode(null);
    console.log('🗑️ Affiliate code cleared');
  };

  // Get affiliate code info for debugging
  const getAffiliateInfo = () => {
    try {
      const stored = localStorage.getItem('affiliateCode');
      if (!stored) return null;
      
      const affiliateData = JSON.parse(stored);
      const ageInHours = Math.floor((Date.now() - affiliateData.timestamp) / (60 * 60 * 1000));
      const ageInMinutes = Math.floor((Date.now() - affiliateData.timestamp) / (60 * 1000));
      
      return {
        ...affiliateData,
        isExpired: isExpired(affiliateData.timestamp),
        ageInHours: ageInHours,
        ageInMinutes: ageInMinutes,
        expiresInHours: Math.max(0, (7 * 24) - ageInHours),
        expiresInMinutes: Math.max(0, (7 * 24 * 60) - ageInMinutes)
      };
    } catch (error) {
      return null;
    }
  };

  return {
    affiliateCode,
    isValidating,
    getAffiliateCode,
    clearAffiliateCode,
    getAffiliateInfo,
    storeAffiliateCode
  };
}
